import { IconRefresh } from "@tabler/icons-react";
import LayoutHR from "layouts/layout-hr";
import React, { useState, useEffect } from "react";
import { getRequest } from "services/api";

const SalaryReportPage = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    return {
      month: now.getMonth() + 1, // JavaScript months are 0-based
      year: now.getFullYear(),
    };
  });

  useEffect(() => {
    fetchSalaryReport();
  }, [filters]);

  const fetchSalaryReport = async () => {
    try {
      setLoading(true);
      const queryString = `?month=${filters.month}&year=${filters.year}`;
      const response = await getRequest(`/ahr/report/salary${queryString}`);
      if (response.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error("Error fetching salary report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefresh = () => {
    fetchSalaryReport();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate summary
  const summary = reportData.reduce((acc, item) => {
    acc.totalBasicSalary += parseFloat(item.basic_salary);
    acc.totalInsurances += parseFloat(item.total_insurances);
    acc.totalFacilities += parseFloat(item.total_facilities);
    acc.totalDeductions += parseFloat(item.total_deductions);
    acc.totalWorkBonuses += parseFloat(item.total_work_bonuses);
    acc.totalOther += parseFloat(item.total_other);
    acc.totalSalary += parseFloat(item.total_salary);
    return acc;
  }, {
    totalBasicSalary: 0,
    totalInsurances: 0,
    totalFacilities: 0,
    totalDeductions: 0,
    totalWorkBonuses: 0,
    totalOther: 0,
    totalSalary: 0,
  });

  if (loading) {
    return (
      <LayoutHR>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Salary Report</h3>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHR>
    );
  }

  return (
    <LayoutHR>
      <div className="container-fluid">
        {/* Header with Filters */}
        <div className="row mb-3 mt-2">
          <div className="col-12">
            <div className="card">
              <div className="card-body py-3">
                <div className="row align-items-center g-3">
                  <div className="col-md-6">
                    <h3 className="card-title mb-1">Salary Report</h3>
                    <div className="text-muted small">
                      Period: {new Date(filters.year, filters.month - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex gap-2 justify-content-end">
                      <div className="input-group input-group-sm" style={{ width: "auto" }}>
                        <select
                          className="form-select"
                          value={filters.month}
                          onChange={(e) => handleFilterChange('month', parseInt(e.target.value))}
                          style={{ minWidth: "120px" }}
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {new Date(0, i).toLocaleDateString('id-ID', { month: 'long' })}
                            </option>
                          ))}
                        </select>
                        <select
                          className="form-select"
                          value={filters.year}
                          onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
                          style={{ minWidth: "100px" }}
                        >
                          {Array.from({ length: 10 }, (_, i) => (
                            <option key={filters.year - 5 + i} value={filters.year - 5 + i}>
                              {filters.year - 5 + i}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleRefresh}
                      >
                        <IconRefresh size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row mb-3">
          <div className="col-lg-2">
            <div className="card">
              <div className="card-body text-center">
                <div className="h5 font-weight-bold text-dark mb-1" style={{ fontSize: '1.1rem' }}>
                  {reportData.length}
                </div>
                <div className="text-muted small">Employees</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2">
            <div className="card">
              <div className="card-body text-center">
                <div className="h5 font-weight-bold text-dark mb-1" style={{ fontSize: '1.1rem' }}>
                  {formatCurrency(summary.totalBasicSalary)}
                </div>
                <div className="text-muted small">Basic Salary</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2">
            <div className="card">
              <div className="card-body text-center">
                <div className="h5 font-weight-bold text-dark mb-1" style={{ fontSize: '1.1rem' }}>
                  {formatCurrency(summary.totalInsurances)}
                </div>
                <div className="text-muted small">Insurances</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2">
            <div className="card">
              <div className="card-body text-center">
                <div className="h5 font-weight-bold text-dark mb-1" style={{ fontSize: '1.1rem' }}>
                  {formatCurrency(summary.totalFacilities)}
                </div>
                <div className="text-muted small">Facilities</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2">
            <div className="card">
              <div className="card-body text-center">
                <div className="h5 font-weight-bold text-dark mb-1" style={{ fontSize: '1.1rem' }}>
                  {formatCurrency(summary.totalDeductions)}
                </div>
                <div className="text-muted small">Deductions</div>
              </div>
            </div>
          </div>
          <div className="col-lg-2">
            <div className="card">
              <div className="card-body text-center">
                <div className="h4 font-weight-bold text-dark mb-1" style={{ fontSize: '1.1rem' }}>
                  {formatCurrency(summary.totalSalary)}
                </div>
                <div className="text-muted small">Total Salary</div>
              </div>
            </div>
          </div>
        </div>

        {/* Salary Report Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Employee Salary Details</h3>
              </div>
              <div className="card-body">
                {reportData.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Salary Date</th>
                          <th className="text-end">Basic Salary</th>
                          <th className="text-end">Insurances</th>
                          <th className="text-end">Facilities</th>
                          <th className="text-end">Deductions</th>
                          <th className="text-end">Work Bonuses</th>
                          <th className="text-end">Other</th>
                          <th className="text-end">Total Salary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.map((item, index) => (
                          <tr key={item.id || index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <div
                                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center text-white"
                                    style={{ width: "40px", height: "40px", fontSize: "16px", fontWeight: "bold" }}
                                  >
                                    {item.user?.name?.charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                <div>
                                  <div className="font-weight-medium">{item.user?.name}</div>
                                  <div className="text-muted small">ID: {item.user_id.slice(-8)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="font-weight-medium">
                                {formatDate(item.date_salary)}
                              </div>
                            </td>
                            <td className="text-end">
                              <span className="font-weight-medium">{formatCurrency(item.basic_salary)}</span>
                            </td>
                            <td className="text-end">
                              <span>{formatCurrency(item.total_insurances)}</span>
                            </td>
                            <td className="text-end">
                              <span>{formatCurrency(item.total_facilities)}</span>
                            </td>
                            <td className="text-end">
                              <span>{formatCurrency(item.total_deductions)}</span>
                            </td>
                            <td className="text-end">
                              <span>{formatCurrency(item.total_work_bonuses)}</span>
                            </td>
                            <td className="text-end">
                              <span>{formatCurrency(item.total_other)}</span>
                            </td>
                            <td className="text-end">
                              <span className="font-weight-bold">{formatCurrency(item.total_salary)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-active">
                          <td colSpan="2" className="font-weight-bold">Total</td>
                          <td className="text-end font-weight-bold">{formatCurrency(summary.totalBasicSalary)}</td>
                          <td className="text-end font-weight-bold">{formatCurrency(summary.totalInsurances)}</td>
                          <td className="text-end font-weight-bold">{formatCurrency(summary.totalFacilities)}</td>
                          <td className="text-end font-weight-bold">{formatCurrency(summary.totalDeductions)}</td>
                          <td className="text-end font-weight-bold">{formatCurrency(summary.totalWorkBonuses)}</td>
                          <td className="text-end font-weight-bold">{formatCurrency(summary.totalOther)}</td>
                          <td className="text-end font-weight-bold">{formatCurrency(summary.totalSalary)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="ti ti-cash display-4 mb-3"></i>
                    <div>No salary data available for the selected period</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHR>
  );
};

export default SalaryReportPage;
